import React, { useEffect, useState, memo, useMemo } from "react";
import GridTemplate from "./GridTemplate";
import "./GridTemplate.css";
import { DeleteConfirmation } from "../LinksComponentDashboard";

import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
  DragStartEvent,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// ─── Sortable wrapper for each grid item ─────────────────────────────────────
const SortableGridItem = memo(({
  item,
  editIndex,
  isEditing,
  username,
  user_id,
  componentName,
  refetch,
  setEditIndex,
  brand_color,
  templates,
  from,
  componentId,
  onRequestDelete,
  onDragStart,
  onDragEnd,
  onImageUploadStatusChange,
  isLayoutCropProcessing,
}: any) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
    // Disable sorting while the card is open in edit mode
    disabled: isEditing,
  });
  const style = {
    transform: CSS.Transform.toString(
      transform ? { ...transform, scaleX: 1, scaleY: 1 } : null
    ),
    transition: isDragging ? undefined : transition,
    opacity: isDragging ? 0.3 : 1,
    willChange: isDragging ? "transform" : undefined,
    zIndex: isDragging ? 999 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`draggable-grid-item ${isEditing ? "editing" : ""}`}
      data-id={item.id}
    >
      <GridTemplate
        card={item}
        username={username}
        user_id={user_id}
        componentName={componentName}
        isDashboard={true}
        refetch={refetch}
        editIndex={editIndex}
        setEditIndex={setEditIndex}
        brand_color={brand_color}
        templates={templates}
        showDelete={true}
        from={from}
        componentId={componentId}
        itemId={item.id}
        onRequestDelete={onRequestDelete}
        // Pass dnd-kit drag handle bindings so the dedicated drag handle button
        // inside GridTemplate becomes the sortable handle.
        dragAttributes={attributes}
        dragListeners={listeners}
        // Pass-through drag callbacks so the drag-handle button inside
        // GridTemplate can call them if it uses the HTML drag API separately.
        // For dnd-kit these are no-ops but kept for API compatibility.
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onImageUploadStatusChange={onImageUploadStatusChange}
        isLayoutCropProcessing={isLayoutCropProcessing}
      />
    </div>
  );
});

// ─── Overlay ghost shown while dragging (lightweight to avoid per-frame jank) ──
// const DragOverlayItem = ({ item }: any) => (
//   <div
//     className="draggable-grid-item"
//     style={{
//       opacity: 0.85,
//       cursor: "grabbing",
//       pointerEvents: "none",
//       willChange: "transform",
//       transform: "scale(1.02)",
//     }}
//   >
//     <div
//       style={{
//         width: "100%",
//         height: "100%",
//         minHeight: "var(--content-square-size, 140px)",
//         background: "rgba(0,0,0,0.05)",
//         borderRadius: 8,
//       }}
//     />
//   </div>
// );

const DragOverlayItem = ({
  item,
  username,
  user_id,
  componentName,
  refetch,
  setEditIndex,
  brand_color,
  templates,
  from,
  componentId,
}: any) => (
  <div
    className="draggable-grid-item"
    style={{
      opacity: 0.95,
      cursor: "grabbing",
      pointerEvents: "none",
      transform: "scale(1.02)",
    }}
  >
    <GridTemplate
      card={item}
      username={username}
      user_id={user_id}
      componentName={componentName}
      isDashboard={true}
      refetch={refetch}
      editIndex={null}
      setEditIndex={setEditIndex}
      brand_color={brand_color}
      templates={templates}
      showDelete={false}
      from={from}
      componentId={componentId}
      itemId={item.id}
    />
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────
const CustomDraggableTemplateGrid = ({
  username,
  user_id,
  componentName,
  refetch,
  editIndex,
  setEditIndex,
  brand_color,
  templates,
  showDelete,
  onRemove,
  isEmptyTemplate,
  name,
  handleReorder,
  from,
  componentId,
  onImageUploadStatusChange,
  layoutCropProcessingIds,
}: any) => {
  const [items, setItems] = useState<any[]>(() =>
    Array.isArray(templates) ? templates : []
  );
  const [activeId, setActiveId] = useState<number | null>(null);

  // Delete modal state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

  useEffect(() => {
    setItems(Array.isArray(templates) ? templates : []);
  }, [templates]);

  // ─── Sensors ───────────────────────────────────────────────────────────────
  // • PointerSensor: require 8px movement before drag starts so normal clicks
  //   (edit, delete buttons) still fire without triggering a drag.
  // • TouchSensor: 200ms hold + 8px tolerance so taps register as taps and
  //   multi-row grids work correctly on mobile.
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 8,
      },
    })
  );

  // ─── Drag handlers ─────────────────────────────────────────────────────────
  const handleDragStart = (event: DragStartEvent) => {
    // Don't allow drag while a card is in edit mode
    if (editIndex !== undefined && editIndex !== null) return;
    setActiveId(Number(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);

    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((item) => item.id === active.id);
    const newIndex = items.findIndex((item) => item.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(items, oldIndex, newIndex);
    setItems(reordered);
    handleReorder(reordered);
  };

  // ─── Delete helpers ────────────────────────────────────────────────────────
  const requestDelete = (id: number) => {
    setPendingDeleteId(id);
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    if (pendingDeleteId === null || pendingDeleteId === undefined) return;
    await onRemove(pendingDeleteId);
    setShowDeleteConfirm(false);
    setPendingDeleteId(null);
    setEditIndex(undefined);
  };

  const activeItem = activeId != null
    ? items.find((item) => item.id === activeId)
    : null;

  const itemIds = useMemo(() => items.map((item) => item.id), [items]);

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={itemIds}
          strategy={rectSortingStrategy}
        >
          {items.map((item) => (
            <SortableGridItem
              key={item.id}
              item={item}
              editIndex={editIndex}
              isEditing={editIndex === item.id}
              username={username}
              user_id={user_id}
              componentName={componentName}
              refetch={refetch}
              setEditIndex={setEditIndex}
              brand_color={brand_color}
              templates={templates}
              from={from}
              componentId={componentId}
              onRequestDelete={requestDelete}
              // no-ops kept for GridTemplate API compatibility
              onDragStart={() => {}}
              onDragEnd={() => {}}
              onImageUploadStatusChange={onImageUploadStatusChange}
              isLayoutCropProcessing={Boolean(
                layoutCropProcessingIds?.has?.(item.id)
              )}
            />
          ))}
        </SortableContext>

        {/* Ghost card that follows the pointer/finger while dragging */}
        <DragOverlay>
          {activeItem ? (
            <DragOverlayItem
              item={activeItem}
              username={username}
              user_id={user_id}
              componentName={componentName}
              refetch={refetch}
              setEditIndex={setEditIndex}
              brand_color={brand_color}
              templates={templates}
              from={from}
              componentId={componentId}
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      {showDeleteConfirm && (
        <DeleteConfirmation
          setShowDeleteConfirm={(v: boolean) => {
            setShowDeleteConfirm(v);
            if (!v) {
              setPendingDeleteId(null);
              setEditIndex(undefined);
            }
          }}
          setEditIndex={setEditIndex}
          handleDelete={handleDelete}
        />
      )}
    </>
  );
};

export default CustomDraggableTemplateGrid;